import * as React from "react";

import * as Sentry from "@sentry/browser";
import {produce} from "immer";

import * as dialogs from "@client/features/dialogs";
import {annotateWithExercises, useWorkout} from "@client/features/workouts";

import * as router from "@client/router";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {Icon} from "@client/components/Icon";
import {ContentModal} from "@client/components/Modal";
import {Workout, uploadVideo} from "@client/components/Workout";
import {ProgramWizard, WorkoutProgram} from "@client/components/WorkoutProgram";
import {migrateClass} from "@client/components/styles";
import {ActionsDefinition} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {ListExerciseType} from "@client/journeys/exercises";
import * as forms from "@client/site/forms";
import {classNames, models, server} from "@reactivated";

const styles = {
    migrate: migrateClass,
} as const;

type WorkoutsProps = models["journal.workouts.Workouts"] & {
    profile: server.core.rpc.Profile.output;
};

export const useWorkouts = (props: WorkoutsProps) => {
    const [isCreatingWorkoutTemplate, setIsCreatingWorkoutTemplate] =
        React.useState(false);
    const [isCreatingExerciseType, setIsCreatingExerciseType] = React.useState(false);
    const [filterQuery, setFilterQuery] = React.useState("");

    const templates = props.templates.filter((workout) =>
        utils.filterPredicate(workout.name, filterQuery),
    );
    const types = props.exercise_types.filter((exerciseType) =>
        utils.filterPredicate(exerciseType.name, filterQuery),
    );

    const filter = (
        <forms.TextInput
            name=""
            flush
            readOnly={false}
            placeholder="Filter workouts and exercises"
            onChange={setFilterQuery}
            value={filterQuery}
        />
    );

    const workoutTemplateForm = forms.useAutoPform(
        server.journal.workouts.CreateWorkoutTemplateForm,
    );
    const exerciseTypeForm = forms.useAutoPform(
        server.journal.workouts.ExerciseTypeForm,
    );

    const createWorkoutTemplate = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const result = await forms.submitForm(
            server.journal.workouts.create_workout_template,
            workoutTemplateForm.values,
            workoutTemplateForm,
        );

        if (result.type == "success") {
            router.push(
                "update_workout_template.workout",
                {
                    workoutId: result.data.id,
                },
                {
                    workouts: produce(props, (draftState) => {
                        draftState.templates.unshift(result.data);
                    }),
                },
            );
        }
    };

    const createExerciseType = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const result = await forms.submitForm(
            server.journal.workouts.create_exercise_type,
            {
                ...exerciseTypeForm.values,
                uuid: crypto.randomUUID(),
                video_url: "",
                video_uid: "",
                description: "",
            },
            exerciseTypeForm,
        );

        if (result.type == "success") {
            router.push(
                "update_exercise_type.index",
                {
                    exerciseTypeId: result.data.id,
                },
                {
                    workouts: produce(props, (draftState) => {
                        draftState.exercise_types.unshift(result.data);
                    }),
                },
            );
        }
    };

    const modals = (
        <>
            <ContentModal
                isOpen={isCreatingExerciseType}
                setOpen={(state) => setIsCreatingExerciseType(state)}
            >
                <form
                    autoComplete="off"
                    onSubmit={createExerciseType}
                    className="flex flex-col gap-4 p-6"
                >
                    <h2 className="text-xl">Create exercise</h2>
                    <forms.PField field={exerciseTypeForm.fields.name} />
                    <forms.PField field={exerciseTypeForm.fields.category} />
                    <button
                        type="submit"
                        disabled={exerciseTypeForm.values.name == ""}
                        className="
                            mt-2 rounded-sm bg-indigo-600 px-2.5 py-1.5 text-sm
                            font-semibold text-white shadow-xs
                            hover:bg-indigo-500
                            focus-visible:outline-2 focus-visible:outline-offset-2
                            focus-visible:outline-indigo-600 focus-visible:outline-solid
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                        "
                    >
                        Create
                    </button>
                </form>
            </ContentModal>
            <ContentModal
                isOpen={isCreatingWorkoutTemplate}
                setOpen={(state) => setIsCreatingWorkoutTemplate(state)}
            >
                <form
                    autoComplete="off"
                    onSubmit={createWorkoutTemplate}
                    className="flex flex-col gap-4 p-6"
                >
                    <h2 className="text-xl">Create workout</h2>
                    <forms.PField field={workoutTemplateForm.fields.name} />
                    <button
                        type="submit"
                        disabled={workoutTemplateForm.values.name == ""}
                        className="
                            mt-2 rounded-sm bg-indigo-600 px-2.5 py-1.5 text-sm
                            font-semibold text-white shadow-xs
                            hover:bg-indigo-500
                            focus-visible:outline-2 focus-visible:outline-offset-2
                            focus-visible:outline-indigo-600 focus-visible:outline-solid
                            disabled:border-slate-200 disabled:bg-slate-50
                            disabled:text-slate-500 disabled:shadow-none
                        "
                    >
                        Create
                    </button>
                </form>
            </ContentModal>
        </>
    );

    const workoutTemplates = (
        <>
            {modals}
            <ul
                role="list"
                className="
                    grid grid-cols-1 gap-6
                    sm:grid-cols-2
                    lg:grid-cols-3
                "
            >
                {templates.map(annotateWithExercises).map((template) => (
                    <li
                        key={template.id}
                        className="
                            relative col-span-1 rounded-lg border border-stone-200
                            bg-white shadow-xs
                        "
                    >
                        <button
                            className="absolute inset-0"
                            onClick={() => {
                                router.push("update_workout_template.workout", {
                                    workoutId: template.id,
                                });
                            }}
                        />
                        <div
                            className="
                                flex w-full items-center justify-between space-x-6 p-6
                            "
                        >
                            <div className="flex-1 truncate">
                                <div className="flex items-center space-x-3">
                                    <h3
                                        className="
                                            truncate text-sm font-medium text-gray-900
                                        "
                                    >
                                        {template.name}
                                    </h3>
                                </div>
                                <p className="mt-1 truncate text-sm text-gray-500">
                                    {template.exercises.length == 1
                                        ? "1 exercise"
                                        : `${template.exercises.length} exercises`}

                                    {template.user_id == null && (
                                        <span
                                            className="
                                                ml-2 inline-flex shrink-0 items-center
                                                rounded-full bg-green-50 px-1.5 py-0.5
                                                text-xs font-medium text-green-700
                                                ring-1 ring-green-600/20 ring-inset
                                            "
                                        >
                                            Sample
                                        </span>
                                    )}

                                    {template.user_id != null &&
                                        template.user_id != props.profile.user.id && (
                                            <span
                                                className="
                                                    ml-2 inline-flex shrink-0
                                                    items-center rounded-full bg-sky-50
                                                    px-1.5 py-0.5 text-xs font-medium
                                                    text-sky-700 ring-1 ring-sky-600/20
                                                    ring-inset
                                                "
                                            >
                                                Shared
                                            </span>
                                        )}
                                </p>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </>
    );

    const exerciseTypes = (
        <div>
            {modals}
            <ul
                role="list"
                className="
                    grid grid-cols-1 gap-6
                    sm:grid-cols-2
                "
            >
                {types
                    .filter(
                        (exerciseType: server.journal.workouts.ExerciseType.output) =>
                            exerciseType.user_id == props.profile.user.id,
                    )
                    .map((exerciseType) => (
                        <ListExerciseType
                            key={exerciseType.id}
                            showCategories
                            exerciseType={exerciseType}
                            profile={props.profile}
                            behavior={{
                                onClick: () => {
                                    router.push("update_exercise_type.index", {
                                        exerciseTypeId: exerciseType.id,
                                    });
                                },
                            }}
                        />
                    ))}
            </ul>
        </div>
    );

    const content = {
        workouts: workoutTemplates,
        exercises: exerciseTypes,
    };

    const actions = {
        workouts: [
            {
                icon: "plus",
                title: "Create",
                onClick: () => {
                    setIsCreatingWorkoutTemplate(true);
                },
            },
        ],
        program: [],
        exercises: [
            {
                icon: "plus",
                title: "Create",
                onClick: () => {
                    setIsCreatingExerciseType(true);
                },
            },
        ],
    } satisfies {[name: string]: ActionsDefinition};

    return {filter, content, actions};
};

export const Workouts = router.Workouts.withHooks((props) => {
    const workouts = useWorkouts({
        ...props.resolved.workouts,
        profile: props.profile,
    });

    const currentProgram = props.resolved.workouts.active_program;
    const pastPrograms = props.resolved.workouts.programs;

    const scope: "client" | "consumer" = props.profile.user.is_client
        ? "client"
        : "consumer";

    return {
        workouts,
        currentProgram,
        pastPrograms,
        scope: scope,
    };
})
    .withTab(
        {
            name: "workouts",
            icon: "custom-dumbbell",
            title: "Templates",
        },
        (props) => props.workouts.content[props.currentTab],
    )
    .withTab(
        {
            name: "program",
            icon: "0814-list2",
            title: "Program",
        },
        (props) => (
            <div className="flex flex-col gap-4">
                {props.currentProgram != null && (
                    <ProgramWizard
                        next_workout={props.resolved.workouts.next_workout}
                        program={props.currentProgram}
                        target={{key: null, user_id: props.profile.user.id}}
                        scope={props.scope}
                        onSelectWorkout={({key}, selected) => {
                            void hardware.startLiveActivity({
                                workout: selected,
                            });
                            router.push("journal.workout", {key});
                        }}
                        onAlreadyHasWorkout={(selectedDate) =>
                            props.notify(
                                `You already have a workout on ${selectedDate}.`,
                                {type: "error", permanent: true},
                            )
                        }
                    />
                )}
                <typography.ImportantAction
                    showIf={props.scope == "consumer"}
                    onClick={() => {
                        router.push(
                            "create_workout_program.index",
                            {},
                            {create_workout_program: props.resolved.workouts},
                        );
                    }}
                    title="Use a program"
                />
                {props.pastPrograms.length > 0 && (
                    <>
                        <h2 className="text-xl">Past Programs</h2>
                        {props.pastPrograms.map((programUser) => (
                            <div
                                key={programUser.id}
                                className="
                                    relative col-span-1 rounded-lg border
                                    border-stone-200 bg-white shadow-xs
                                "
                            >
                                <div
                                    className="
                                        flex w-full items-center justify-between
                                        space-x-6 p-4
                                    "
                                >
                                    <div className="flex-1 truncate">
                                        <div className="flex items-center space-x-3">
                                            <h3
                                                className="
                                                    truncate text-sm font-medium
                                                    text-gray-900
                                                "
                                            >
                                                {programUser.program.name ??
                                                    "Custom program"}
                                            </h3>
                                        </div>
                                        <p
                                            className="
                                                mt-1 truncate text-sm text-gray-500
                                            "
                                        >
                                            {programUser.completed_on != null ? (
                                                <span>
                                                    {" "}
                                                    Completed on{" "}
                                                    {utils.formatDate(
                                                        programUser.completed_on,
                                                    )}
                                                </span>
                                            ) : (
                                                "Incomplete"
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        ),
    )
    .withTab(
        {
            name: "exercises",
            icon: "0611-bench-press",
            title: "My Exercises",
        },
        (props) => props.workouts.content[props.currentTab],
    )
    .withActions((props) => props.workouts.actions[props.currentTab])
    .withOptions((props) => ({
        title: "Workouts",
        topSection: props.currentTab != "program" ? props.workouts.filter : null,
    }));

export const CreateWorkoutProgram = router.CreateWorkoutProgram.withHooks(() => {})
    .withIndex((props) => (
        <WorkoutProgram
            profile={props.profile}
            exercise_types={props.resolved.create_workout_program.exercise_types}
            exercise_maxes={props.resolved.create_workout_program.exercise_maxes}
        />
    ))
    .withActions(() => [])
    .withOptions(() => ({title: "Workout Programming"}));

export const CreateWorkout = router.CreateWorkout.withHooks(() => {})
    .withIndex(() => <div>Ok</div>)
    .withActions(() => [])
    .withOptions(() => ({title: "Create workout"}));

export const UpdateWorkoutTemplate = router.UpdateWorkoutTemplate.withHooks((props) => {
    const handlers = useWorkout({
        workout: props.resolved.update_workout_template,
    });

    const workout = (
        <Workout
            workout_uuid={props.resolved.update_workout_template.uuid}
            handlers={handlers}
            isTemplate
        />
    );

    const form = forms.useAutoPform(
        server.journal.workouts.WorkoutTemplateForm,
        props.resolved.update_workout_template,
    );

    const [initial] = React.useState({sections: handlers.sections});

    const isDirty =
        JSON.stringify(initial.sections) != JSON.stringify(handlers.sections) ||
        JSON.stringify(form.initial) != JSON.stringify(form.values);

    const bottomSection = isDirty ? (
        <div className={styles.migrate}>
            <div className="ml-2 flex items-center text-base">Don’t forget to save</div>
        </div>
    ) : null;

    return {
        sections: handlers.sections,
        workout,
        form,
        isDirty,
        bottomSection,
    };
})
    .withTab(
        {
            name: "workout",
            icon: "custom-dumbbell",
            title: "Workout",
        },
        (props) => props.workout,
    )
    .withTab(
        {
            name: "details",
            icon: "pencil-square-o",
            title: "Details",
            hasErrors: (props) => props.form.errors != null,
        },
        (props) => (
            <div className="flex flex-col gap-4">
                <forms.PField field={props.form.fields.name} />
                <forms.PField field={props.form.fields.instructions} />
                <forms.PField
                    field={{
                        ...props.form.fields.visibility,
                        disabledOptions: ["PUBLIC"],
                    }}
                />
                {/*
                <forms.Fields
                    form={props.form}
                    exclude={
                        props.profile.user.is_manager == false ? ["visibility"] : []
                    }
                />
                */}
            </div>
        ),
    )
    .withActions((props) => [
        {
            icon: "save",
            onClick: async () => {
                const response = await forms.submitForm(
                    server.journal.workouts.update_workout,
                    {
                        uuid: props.resolved.update_workout_template.uuid,
                        sections: props.sections,
                        questions: [],
                        template: {...props.form.values, maxes: "EXISTING_MAXES"},
                    },
                    props.form,
                    "template",
                );

                if (response.type === "success") {
                    props.notify("Your workout template was updated");
                    router.push("workouts.workouts", {});
                }
            },
            title: "Save",
        },
        {
            icon: "remove",
            onClick: async () => {
                const confirmed = await props.dispatch(
                    dialogs.confirm({
                        message: "Are you sure?",
                        body: "This template will be deleted forever.",
                    }),
                );

                if (confirmed) {
                    const result =
                        await server.journal.workouts.delete_workout_template({
                            source_workout: props.resolved.update_workout_template.uuid,
                        });

                    if (result.type == "success") {
                        props.notify("Your template was deleted");
                        router.push("workouts.workouts", {}, {workouts: result.data});
                    }
                }
            },
            title: "Delete",
        },
    ])
    .withOptions((props) => ({
        title: props.resolved.update_workout_template.name,
        bottomSection: props.bottomSection,
        back: "Workouts",
    }));

export const UpdateExerciseType = router.UpdateExerciseType.withHooks((props) => {
    const exerciseType = props.resolved.update_exercise_type.exerciseType;
    const form = forms.useAutoPform(
        server.journal.workouts.ExerciseTypeForm,
        {
            name: exerciseType.name,
            category: exerciseType.category,
            video_url: exerciseType.video_url,
            video_uid: exerciseType.video_uid ?? "",
            description: exerciseType.description,
        },
        {overrides: {category: {readOnly: true}}},
    );

    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [videoType, setVideoType] = React.useState<"HOSTED" | "EXTERNAL">(
        props.resolved.update_exercise_type.exerciseType.video_uid != null
            ? "HOSTED"
            : "EXTERNAL",
    );

    const pForm = forms.usePform(
        {
            video_type: {
                label: "Video",
                type: "select",
                options: [
                    ["EXTERNAL", "Link"],
                    ["HOSTED", "Hosted"],
                ],
                required: true,
            },
        },
        {
            video_type: videoType,
        },
        (values) => setVideoType(values.video_type),
    );

    const videoDialog = dialogs.useDialog({
        Content: () => (
            <div>
                <iframe
                    src={`https://customer-zze5w8c396zl9f2i.cloudflarestream.com/${props
                        .resolved.update_exercise_type.exerciseType.video_uid!}/iframe`}
                    style={{border: "none"}}
                    className="aspect-video w-[95vw] max-w-[1024px]"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                />
            </div>
        ),
    });
    const [progress, setProgress] = React.useState<string | null>(null);

    return {
        handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedFile(event.target.files?.[0] ?? null);
        },
        videoDialog,
        pForm,
        form,
        selectedFile,
        progress,
        setProgress,
    };
})
    .withIndex((props) => (
        <div className="flex flex-col gap-4">
            {props.videoDialog.dialog}
            <forms.PField field={props.form.fields.name} />
            <forms.PField field={props.form.fields.category} />
            <forms.PField field={props.form.fields.description} />
            {props.profile.user.is_manager && (
                <>
                    <forms.PField field={props.pForm.fields.video_type} />
                    {props.pForm.values.video_type == "EXTERNAL" ? (
                        <forms.PField field={props.form.fields.video_url} />
                    ) : (
                        <div
                            className="
                                flex flex-col gap-2
                                sm:flex-row sm:items-center
                            "
                        >
                            <div
                                className="
                                    relative
                                    sm:flex-1
                                "
                            >
                                <input
                                    className={classNames(
                                        props.progress != null && "invisible",
                                    )}
                                    type="file"
                                    accept="video/*"
                                    onChange={props.handleFileChange}
                                />

                                {props.progress != null && (
                                    <div className="absolute inset-0 flex items-center">
                                        <div
                                            className="
                                                h-2.5 w-full rounded-full bg-stone-200
                                                dark:bg-gray-700
                                            "
                                        >
                                            <div
                                                className="
                                                    h-2.5 rounded-full bg-blue-600
                                                "
                                                style={{width: `${props.progress}%`}}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {props.resolved.update_exercise_type.exerciseType
                                .video_uid != null && (
                                <div className="shrink-0">
                                    <button
                                        type="button"
                                        className="
                                            rounded-sm bg-white px-2 py-1 text-xs
                                            font-semibold text-gray-900 shadow-xs ring-1
                                            ring-gray-300 ring-inset
                                            hover:bg-stone-50
                                        "
                                        onClick={props.videoDialog}
                                    >
                                        <Icon icon="0875-play-circle">
                                            Current video
                                        </Icon>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    ))
    .withActions((props) => [
        {
            icon: "save",
            onClick: async () => {
                let video_url = props.form.values.video_url;
                let video_uid = props.form.values.video_uid;

                if (props.selectedFile != null) {
                    const file = props.selectedFile;

                    props.dispatch({
                        type: "INCREMENT_REQUESTS",
                    });
                    video_url = "";

                    try {
                        video_uid = await uploadVideo({
                            file,
                            onProgress: props.setProgress,
                            user_id: props.profile.user.id,
                        });
                    } catch (error: unknown) {
                        Sentry.captureException(error);
                    } finally {
                        props.dispatch({
                            type: "DECREMENT_REQUESTS",
                        });
                    }
                }

                const result = await forms.submitForm(
                    server.journal.workouts.update_exercise_type,
                    {
                        ...props.form.values,
                        uuid: props.resolved.update_exercise_type.exerciseType.uuid,
                        video_url,
                        video_uid,
                    },
                    props.form,
                );

                if (result.type == "success") {
                    props.notify("Your exercise was updated");
                    router.push("workouts.exercises", {});
                }
            },
            title: "Save",
        },
        {
            icon: "remove",
            onClick: async () => {
                const confirmed = await props.dispatch(
                    dialogs.confirm({
                        message: "Are you sure?",
                        body: "This exercise will be deleted forever.\n\nYour existing workouts will not be affected.",
                    }),
                );

                if (confirmed) {
                    await server.journal.workouts.delete_exercise_type({
                        source_exercise_type:
                            props.resolved.update_exercise_type.exerciseType.uuid,
                    });

                    props.notify("Your exercise was deleted");
                    router.push("workouts.workouts", {});
                }
            },
            title: "Delete",
        },
    ])
    .withOptions((props) => ({
        title: props.resolved.update_exercise_type.exerciseType.name,
        back: {
            locator: router.locator({
                route: "workouts.exercises",
                resolved: props.resolved,
                params: {},
            }),
            title: "Exercises",
        },
    }));
